import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import UserContext from './UserContext';
import styles from './Detective.module.css';
import { CHAPTERS, UPCOMING } from './detectiveChapters';

const TYPE_SPEED = 26;

const TONES = {
  me: styles.toneMe,
  inner: styles.toneInner,
  gray: styles.toneGray,
  amber: styles.toneAmber,
  rose: styles.toneRose,
  green: styles.toneGreen,
  violet: styles.toneViolet,
  orange: styles.toneOrange,
  blue: styles.toneBlue,
  red: styles.toneRed,
};

const newProgress = (chapter) => ({
  phase: 'intro',
  loc: chapter.META.startLoc,
  clues: [],
  seen: [],
  flags: [],
  step: 0,
  wrong: [],
  ending: null,
});

const loadProgress = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    if (!saved || !saved.phase) return null;
    // Saves from before endings had names.
    if (saved.phase === 'solved') return { ...saved, phase: 'ended', ending: 'true' };
    if (saved.phase === 'failed') return { ...saved, phase: 'ended', ending: 'bad' };
    return saved;
  } catch (error) {
    return null;
  }
};

const loadCleared = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || [];
  } catch (error) {
    return [];
  }
};

// A condition is { clue }, { flag } or a list of those that must all hold.
const holds = (when, progress) => {
  if (!when) return true;
  if (Array.isArray(when)) return when.every((w) => holds(w, progress));
  if (when.clue) return progress.clues.includes(when.clue);
  if (when.flag) return progress.flags.includes(when.flag);
  return true;
};

// The site owner's account can switch on a test mode. Only a fingerprint of the address is kept here.
const OWNER_HASH = 'e3c2e36a26f9e33128b48787be4cc5b2586dd0ef328b98032636918db85c6b88';

const sha256 = async (text) => {
  const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Test mode: every clue in hand and every flag a scene can raise, except flags that send someone
// away. Story events are left to play on their own as the tester moves around.
const withEverything = (chapter, progress) => {
  const list = (when) => (!when ? [] : [].concat(when));
  const flags = new Set(progress.flags);
  const note = (item) => { if (item.flag) flags.add(item.flag); };
  chapter.LOCATIONS.forEach((l) => l.hotspots.forEach((h) => h.variants.forEach(note)));
  chapter.PEOPLE.forEach((p) => { p.topics.forEach(note); Object.values(p.present).forEach(note); });
  chapter.PEOPLE.forEach((p) => list(p.goneWhen).forEach((w) => flags.delete(w.flag)));
  return { ...progress, clues: Object.keys(chapter.CLUES), flags: [...flags] };
};

// Wrong answers already tried in the deduction, remembered as "step:answer".
const wrongKey = (step, value) => `${step}:${value}`;
const wrongOf = (progress) => progress.wrong || [];

const clockText = (clock, found, total) => {
  const minutes = Math.round(clock.start + ((clock.end - clock.start) * found) / total);
  const day = clock.days[Math.min(Math.floor(minutes / 1440), clock.days.length - 1)];
  const hh = String(Math.floor((minutes % 1440) / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return `${day} ${hh}:${mm}`;
};

function DetectiveGame({ chapter, saveKey, testMode, hasNext, onNext, onExit, onCleared }) {
  const {
    META, SPEAKERS, CLUES, INTRO, LOCATIONS, PEOPLE, VICTIM, EVENTS, DEDUCTION, ENDINGS,
    EXTRAS = [], Map: ChapterMap,
  } = chapter;

  const [savedGame] = useState(() => loadProgress(saveKey));
  const [progress, setProgress] = useState(null); // null while the title screen is up
  const [script, setScript] = useState(null);     // { lines, index, effects } while text is playing
  const [typed, setTyped] = useState(0);
  const [talkTo, setTalkTo] = useState(null);
  const [picking, setPicking] = useState(false);
  const [notebook, setNotebook] = useState(null); // null | 'clues' | 'people'
  const [toasts, setToasts] = useState([]);
  const advanceRef = useRef(null);

  const line = script ? script.lines[script.index] : null;
  const closing = Boolean(script && script.effects.phase === 'ended');
  const solved = progress?.ending === 'true';

  useEffect(() => {
    if (!progress) return;
    try {
      localStorage.setItem(saveKey, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving detective progress:', error);
    }
  }, [progress, saveKey]);

  useEffect(() => {
    if (solved) onCleared(META.id);
    // onCleared is recreated on every render of the case list; the chapter id is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solved, META.id]);

  useEffect(() => {
    if (!line || typed >= line.text.length) return;
    const timer = setTimeout(() => setTyped((count) => count + 1), TYPE_SPEED);
    return () => clearTimeout(timer);
  }, [line, typed]);

  useEffect(() => {
    if (!toasts.length) return;
    const timer = setTimeout(() => setToasts([]), 3200);
    return () => clearTimeout(timer);
  }, [toasts]);

  useEffect(() => {
    if (!script) return;
    const onKey = (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        advanceRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [script]);

  // Who is talking, as shown above each line. Unlabelled narration is the detective's inner voice.
  const speakerOf = (who) => {
    if (who === 'sys') return { name: '안내', tone: 'gray' };
    if (who === 'narr') return { name: SPEAKERS.me.name, title: '속마음', tone: 'inner' };
    return SPEAKERS[who];
  };

  const applyEffects = (effects) => {
    const gives = (effects.gives || []).filter((id) => !progress.clues.includes(id));
    const next = {
      ...progress,
      clues: [...progress.clues, ...gives],
      seen: effects.seen && !progress.seen.includes(effects.seen) ? [...progress.seen, effects.seen] : progress.seen,
      flags: effects.flag && !progress.flags.includes(effects.flag) ? [...progress.flags, effects.flag] : progress.flags,
      phase: effects.phase || progress.phase,
      step: effects.step === undefined ? progress.step : effects.step,
      wrong: effects.wrong && !wrongOf(progress).includes(effects.wrong)
        ? [...wrongOf(progress), effects.wrong]
        : wrongOf(progress),
      ending: effects.ending === undefined ? progress.ending : effects.ending,
    };
    const opened = LOCATIONS.filter((l) => !holds(l.showWhen, progress) && holds(l.showWhen, next));
    setProgress(next);
    setToasts([
      ...gives.map((id) => `${CLUES[id].kind === '기억' ? '기억' : '단서 획득'} — ${CLUES[id].name}`),
      ...opened.map((l) => `새 장소 — ${l.name}`),
    ]);
  };

  const play = (lines, effects = {}) => {
    // Space advances the text; without this it would also re-press whatever was just clicked.
    if (document.activeElement) document.activeElement.blur();
    setScript({ lines, index: 0, effects });
    setTyped(0);
  };

  const advance = () => {
    if (!script) return;
    if (typed < line.text.length) {
      setTyped(line.text.length);
      return;
    }
    if (script.index < script.lines.length - 1) {
      setScript({ ...script, index: script.index + 1 });
      setTyped(0);
      return;
    }
    setScript(null);
    applyEffects(script.effects);
  };
  advanceRef.current = advance;

  const startNew = () => {
    setProgress(testMode ? withEverything(chapter, newProgress(chapter)) : newProgress(chapter));
    setTalkTo(null);
    setPicking(false);
    play(INTRO, { phase: 'investigate' });
  };

  const resume = () => {
    setProgress(testMode ? withEverything(chapter, savedGame) : savedGame);
    if (savedGame.phase === 'intro') play(INTRO, { phase: 'investigate' });
  };

  const goTo = (locId) => {
    if (script) return;
    setTalkTo(null);
    setPicking(false);
    setProgress((p) => ({ ...p, loc: locId }));
    const event = EVENTS.find((e) => (
      progress.clues.length >= e.cluesAtLeast && holds(e.when, progress) && !progress.flags.includes(e.id)
    ));
    if (event) play(event.lines, { flag: event.id });
  };

  const variantOf = (spot) => {
    const index = spot.variants.findIndex((v) => holds(v.when, progress));
    return { variant: spot.variants[index], key: `${progress.loc}.${spot.id}.${index}` };
  };

  const examine = (spot) => {
    if (script) return;
    const { variant, key } = variantOf(spot);
    play(variant.lines, { gives: variant.gives, flag: variant.flag, seen: key });
  };

  const ask = (person, topic) => {
    if (script) return;
    play(topic.lines, { gives: topic.gives, flag: topic.flag, seen: `${person.id}.${topic.id}` });
  };

  const present = (person, clueId) => {
    if (script) return;
    setPicking(false);
    const reaction = person.present[clueId];
    if (reaction) {
      play(reaction.lines, { gives: reaction.gives, flag: reaction.flag, seen: `${person.id}>${clueId}` });
    } else {
      play(person.fallback);
    }
  };

  const enterStep = (index, lines) => {
    setTalkTo(null);
    setPicking(false);
    setProgress((p) => ({ ...p, phase: 'deduce', step: index, ending: null }));
    play([...lines, ...DEDUCTION.steps[index].prompt]);
  };

  const startDeduction = () => {
    if (script) return;
    // The door opens once there is something to put on the table at every evidence step.
    const stuck = DEDUCTION.steps.find((s) => (
      s.type === 'evidence' && !s.accept.some((id) => progress.clues.includes(id))
    ));
    if (stuck) {
      const hinted = DEDUCTION.required.find((r) => stuck.accept.includes(r.clue));
      const hint = hinted ? hinted.hint : `「${stuck.label}」에 내놓을 증거가 아직 없다.`;
      play([{ who: 'narr', text: '아직 이르다. 퍼즐 조각이 모자란다.' }, { who: 'narr', text: hint }]);
      return;
    }
    enterStep(0, DEDUCTION.opening);
  };

  // After a failed ending: back to the top of the deduction, or of the confrontation.
  const retry = () => {
    if (script) return;
    if (ENDINGS[progress.ending].retry === 'confront') {
      enterStep(DEDUCTION.steps.findIndex((s) => s.sudden), []);
    } else {
      startDeduction();
    }
  };

  const answer = (value) => {
    if (script) return;
    const step = DEDUCTION.steps[progress.step];
    if (step.accept.includes(value)) {
      const next = DEDUCTION.steps[progress.step + 1];
      if (next) play([...step.success, ...next.prompt], { step: progress.step + 1 });
      else play([...step.success, ...ENDINGS.true.lines], { phase: 'ended', ending: 'true' });
      return;
    }
    // A wrong pick costs nothing; it is crossed out so the next try is easier.
    const wrong = wrongKey(progress.step, value);
    if (step.near && step.near[value]) {
      play(step.near[value], { wrong });
      return;
    }
    const reply = (step.rebuttal && step.rebuttal[value]) || DEDUCTION.wrong;
    if (step.sudden) {
      play([...reply, ...ENDINGS[step.sudden].lines], { phase: 'ended', ending: step.sudden });
    } else {
      play(reply, { wrong });
    }
  };

  const isOpen = (locId) => holds(LOCATIONS.find((l) => l.id === locId).showWhen, progress);
  const names = Object.fromEntries(LOCATIONS.map((l) => [l.id, l.name]));
  // Someone with `goneWhen` leaves their place once that condition holds.
  const isHere = (person) => !person.goneWhen || !holds(person.goneWhen, progress);
  const countAt = (locId) => PEOPLE.filter((p) => p.loc === locId && isHere(p)).length;

  const renderClueGrid = (onPick, isTried = () => false, isAnswer = () => false) => (
    <div className={styles.clueGrid}>
      {progress.clues.filter((id) => CLUES[id].kind !== '기억').map((id) => (
        <button
          key={id}
          className={`${styles.clueChip} ${isTried(id) ? styles.tried : ''} ${isAnswer(id) ? styles.answer : ''}`}
          onClick={() => onPick(id)}
        >
          <span className={styles.clueKind}>{CLUES[id].kind}</span>
          {CLUES[id].name}
        </button>
      ))}
    </div>
  );

  const renderPersonCard = (person, onClick, tried = false, right = false) => (
    <button
      key={person.id}
      className={`${styles.personCard} ${tried ? styles.tried : ''} ${right ? styles.answer : ''}`}
      onClick={onClick}
    >
      <span className={styles.personName}>{person.name}</span>
      <span className={styles.personRole}>{person.age ? `${person.age}세 · ` : ''}{person.role}</span>
      <span className={styles.tag}>{person.tag}</span>
    </button>
  );

  const renderLocation = () => {
    const location = LOCATIONS.find((l) => l.id === progress.loc);
    const here = PEOPLE.filter((p) => p.loc === location.id && isHere(p));
    return (
      <>
        <h2 className={styles.sceneTitle}>{location.name}</h2>
        <p className={styles.sceneDesc}>{location.desc}</p>
        <h3 className={styles.sectionLabel}>조사</h3>
        <div className={styles.actionList}>
          {location.hotspots.filter((spot) => holds(spot.showWhen, progress)).map((spot) => {
            const done = progress.seen.includes(variantOf(spot).key);
            return (
              <button
                key={spot.id}
                className={`${styles.action} ${done ? styles.actionDone : ''}`}
                onClick={() => examine(spot)}
              >
                {spot.label}
              </button>
            );
          })}
        </div>
        {here.length > 0 && (
          <>
            <h3 className={styles.sectionLabel}>이곳에 있는 사람</h3>
            <div className={styles.personList}>
              {here.map((person) => renderPersonCard(person, () => setTalkTo(person.id)))}
            </div>
          </>
        )}
      </>
    );
  };

  const renderInterrogation = () => {
    const person = PEOPLE.find((p) => p.id === talkTo);
    return (
      <>
        <button className={styles.backLink} onClick={() => { setTalkTo(null); setPicking(false); }}>
          ← 돌아가기
        </button>
        <h2 className={styles.sceneTitle}>
          {person.name} <span className={styles.tag}>{person.tag}</span>
        </h2>
        <p className={styles.sceneMeta}>{person.age ? `${person.age}세 · ` : ''}{person.role}</p>
        <p className={styles.sceneDesc}>{person.desc}</p>
        <h3 className={styles.sectionLabel}>질문</h3>
        <div className={styles.actionList}>
          {person.topics.filter((topic) => holds(topic.when, progress)).map((topic) => {
            const done = progress.seen.includes(`${person.id}.${topic.id}`);
            return (
              <button
                key={topic.id}
                className={`${styles.action} ${done ? styles.actionDone : ''}`}
                onClick={() => ask(person, topic)}
              >
                {topic.label}
              </button>
            );
          })}
        </div>
        <h3 className={styles.sectionLabel}>증거</h3>
        {picking ? (
          <>
            <p className={styles.hint}>무엇을 내밀까.</p>
            {renderClueGrid((id) => present(person, id))}
          </>
        ) : (
          <button className={styles.action} onClick={() => setPicking(true)} disabled={!progress.clues.length}>
            증거를 내민다
          </button>
        )}
      </>
    );
  };

  const renderDeduction = () => {
    const step = DEDUCTION.steps[progress.step];
    const reasoning = DEDUCTION.steps.filter((s) => !s.stage).length;
    const everyone = [VICTIM, ...PEOPLE];
    const tried = (value) => wrongOf(progress).includes(wrongKey(progress.step, value));
    // In test mode the accepted answers are marked.
    const right = (value) => Boolean(testMode) && step.accept.includes(value);
    return (
      <main className={styles.deduce}>
        <p className={styles.deduceCount}>
          {step.stage || `추리 ${progress.step + 1} / ${reasoning}`}
        </p>
        {step.sudden && <p className={styles.sudden}>한 번의 실수로 끝난다.</p>}
        <h2 className={styles.deduceLabel}>{step.label}</h2>
        {step.brief && <p className={styles.deduceBrief}>{step.brief}</p>}
        {step.type === 'evidence' && renderClueGrid(answer, tried, right)}
        {step.type === 'culprit' && (
          <div className={`${styles.personList} ${styles.culpritList}`}>
            {step.candidates.map((id) => {
              const person = everyone.find((p) => p.id === id);
              return renderPersonCard(person, () => answer(id), tried(id), right(id));
            })}
          </div>
        )}
        {step.type === 'choice' && (
          <div className={styles.actionList}>
            {step.options.map((option) => (
              <button
                key={option.id}
                className={`${styles.action} ${tried(option.id) ? styles.tried : ''} ${right(option.id) ? styles.answer : ''}`}
                onClick={() => answer(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
        {!step.sudden && (
          <button
            className={styles.backLink}
            onClick={() => setProgress((p) => ({ ...p, phase: 'investigate' }))}
          >
            ← 조사로 돌아가기
          </button>
        )}
      </main>
    );
  };

  const renderEnding = () => {
    const ending = ENDINGS[progress.ending];
    return (
      <main className={styles.ending}>
        <p className={styles.endingKicker}>사건 종결</p>
        <h2 className={styles.endingTitle}>{ending.title}</h2>
        <p className={styles.endingText}>{ending.epilogue}</p>
        <p className={styles.endingStat}>
          모은 단서 {progress.clues.length} / {Object.keys(CLUES).length}
        </p>
        {solved ? (
          <div className={styles.buttonRow}>
            <button className={styles.primaryButton} onClick={hasNext ? onNext : onExit}>
              {hasNext ? '다음 사건으로' : '사건 목록으로'}
            </button>
            <button className={styles.secondaryButton} onClick={startNew}>처음부터 다시</button>
          </div>
        ) : (
          <div className={styles.buttonRow}>
            <button className={styles.primaryButton} onClick={retry}>
              {ending.retry === 'confront'
                ? `${DEDUCTION.steps.find((s) => s.sudden).stage} 장면부터 다시`
                : '다시 추리하기'}
            </button>
            <button
              className={styles.secondaryButton}
              onClick={() => setProgress((p) => ({ ...p, phase: 'investigate', ending: null }))}
            >
              조사로 돌아가기
            </button>
          </div>
        )}
      </main>
    );
  };

  const renderNotebook = () => {
    // People met so far: nobody from a place the case has not led to yet.
    const known = [
      VICTIM,
      ...PEOPLE.filter((person) => isOpen(person.loc)),
      ...EXTRAS.filter((extra) => holds(extra.when, progress)),
    ];
    return (
      <div className={styles.overlay} onClick={() => setNotebook(null)}>
        <div className={styles.notebook} onClick={(event) => event.stopPropagation()}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${notebook === 'clues' ? styles.tabActive : ''}`}
              onClick={() => setNotebook('clues')}
            >
              단서 {progress.clues.length}
            </button>
            <button
              className={`${styles.tab} ${notebook === 'people' ? styles.tabActive : ''}`}
              onClick={() => setNotebook('people')}
            >
              인물
            </button>
            <button className={styles.closeButton} onClick={() => setNotebook(null)}>닫기</button>
          </div>
          <div className={styles.notebookBody}>
            {notebook === 'clues' && !progress.clues.length && (
              <p className={styles.hint}>아직 적어 둔 것이 없다.</p>
            )}
            {notebook === 'clues' && progress.clues.map((id) => (
              <div key={id} className={styles.entry}>
                <p className={styles.entryName}>
                  <span className={styles.clueKind}>{CLUES[id].kind}</span>
                  {CLUES[id].name}
                </p>
                <p className={styles.entryText}>{CLUES[id].text}</p>
              </div>
            ))}
            {notebook === 'people' && known.map((person) => (
              <div key={person.name} className={styles.entry}>
                <p className={styles.entryName}>
                  <span className={styles.tag}>{person.tag}</span>
                  {person.name}{' '}
                  <span className={styles.entryMeta}>{person.age ? `${person.age}세 · ` : ''}{person.role}</span>
                </p>
                <p className={styles.entryText}>{person.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderDialogue = () => {
    const speaker = speakerOf(line.who);
    return (
      <div className={styles.dialogueBackdrop} onClick={advance}>
        <div className={`${styles.dialogue} ${TONES[speaker.tone]}`}>
          <p className={styles.speaker}>
            {speaker.name}
            {speaker.title && <span className={styles.speakerTitle}>{speaker.title}</span>}
          </p>
          <p className={line.who === 'narr' ? styles.narration : styles.speech}>
            {line.text.slice(0, typed)}
            <span className={styles.untyped}>{line.text.slice(typed)}</span>
          </p>
          <span className={`${styles.nextMark} ${typed >= line.text.length ? styles.nextMarkOn : ''}`}>▼</span>
        </div>
      </div>
    );
  };

  if (!progress) {
    return (
      <div className={styles.detectiveContainer}>
        <div className={styles.mist} />
        <main className={styles.title}>
          <p className={styles.titleKicker}>사건 파일 {META.number}</p>
          <h1 className={styles.titleHanja}>{META.hanja}</h1>
          <p className={styles.titleName}>{META.name} — {META.subtitle}</p>
          <p className={styles.titleTagline}>{META.tagline}</p>
          <div className={styles.buttonRow}>
            <button className={styles.primaryButton} onClick={startNew}>처음부터</button>
            {savedGame && (
              <button className={styles.secondaryButton} onClick={resume}>이어하기</button>
            )}
          </div>
          <button className={styles.backLink} onClick={onExit}>← 사건 목록</button>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.detectiveContainer}>
      <div className={styles.mist} />
      <header className={styles.topBar}>
        <div className={styles.brand}>
          <span className={styles.brandHanja}>{META.hanja}</span>
          <span className={styles.brandName}>{META.subtitle}</span>
          {testMode && <span className={styles.testTag}>테스트 모드</span>}
        </div>
        <div className={styles.clock}>
          {clockText(META.clock, progress.clues.length, Object.keys(CLUES).length)}
          <span className={styles.clockLimit}>{META.clock.limit}</span>
        </div>
        <div className={styles.barButtons}>
          <button className={styles.barButton} onClick={() => setNotebook('clues')}>
            수첩 {progress.clues.length}
          </button>
          {progress.phase === 'investigate' && (
            <button className={`${styles.barButton} ${styles.barButtonPrimary}`} onClick={startDeduction}>
              추리하기
            </button>
          )}
          <button className={styles.barButton} onClick={onExit}>나가기</button>
        </div>
      </header>

      {progress.phase === 'ended' && renderEnding()}
      {progress.phase === 'deduce' && !closing && renderDeduction()}
      {(progress.phase === 'investigate' || progress.phase === 'intro') && (
        <main className={styles.board}>
          <section className={styles.mapPanel}>
            <ChapterMap current={progress.loc} onSelect={goTo} isOpen={isOpen} names={names} countAt={countAt} />
            <p className={styles.hint}>장소를 눌러 이동합니다.</p>
          </section>
          <section className={styles.scenePanel}>
            {talkTo && isHere(PEOPLE.find((p) => p.id === talkTo)) ? renderInterrogation() : renderLocation()}
          </section>
        </main>
      )}

      {toasts.length > 0 && (
        <div className={styles.toasts}>
          {toasts.map((text) => (
            <p key={text} className={styles.toast}>{text}</p>
          ))}
        </div>
      )}
      {notebook && renderNotebook()}
      {script && renderDialogue()}
    </div>
  );
}

// The case list: one boat on the home page opens this, and each case opens from here.
function Detective() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const owner = user?.email || 'guest';
  const clearedKey = `detective_cleared_${owner}`;
  const saveKeyOf = (chapter) => `${chapter.META.saveKey}_${owner}`;

  const [openId, setOpenId] = useState(null);
  const [cleared, setCleared] = useState(() => loadCleared(clearedKey));

  // Test mode is offered to the owner's account only, and can be switched off to play normally.
  const testKey = `detective_test_${owner}`;
  const [isOwner, setIsOwner] = useState(false);
  const [testOn, setTestOn] = useState(() => {
    try {
      return localStorage.getItem(testKey) !== 'off';
    } catch (error) {
      return true;
    }
  });
  const testMode = isOwner && testOn;

  useEffect(() => {
    let current = true;
    if (!user?.email || !window.crypto?.subtle) {
      setIsOwner(false);
      return undefined;
    }
    sha256(user.email.trim().toLowerCase()).then((hash) => {
      if (current) setIsOwner(hash === OWNER_HASH);
    });
    return () => { current = false; };
  }, [user]);

  const toggleTest = () => {
    const next = !testOn;
    setTestOn(next);
    try {
      localStorage.setItem(testKey, next ? 'on' : 'off');
    } catch (error) {
      console.error('Error saving test mode:', error);
    }
  };

  const markCleared = (id) => {
    if (cleared.includes(id)) return;
    const next = [...cleared, id];
    setCleared(next);
    try {
      localStorage.setItem(clearedKey, JSON.stringify(next));
    } catch (error) {
      console.error('Error saving cleared chapters:', error);
    }
  };

  const openIndex = CHAPTERS.findIndex((c) => c.META.id === openId);
  if (openIndex >= 0) {
    const chapter = CHAPTERS[openIndex];
    const nextChapter = CHAPTERS[openIndex + 1];
    return (
      <DetectiveGame
        key={chapter.META.id}
        chapter={chapter}
        saveKey={saveKeyOf(chapter)}
        testMode={testMode}
        hasNext={Boolean(nextChapter)}
        onNext={() => setOpenId(nextChapter.META.id)}
        onExit={() => setOpenId(null)}
        onCleared={markCleared}
      />
    );
  }

  const status = CHAPTERS.map((chapter) => {
    const saved = loadProgress(saveKeyOf(chapter));
    const done = cleared.includes(chapter.META.id) || saved?.ending === 'true';
    return { saved, done };
  });

  return (
    <div className={styles.detectiveContainer}>
      <div className={styles.mist} />
      <main className={styles.hub}>
        <p className={styles.titleKicker}>미스터리 스릴러</p>
        <h1 className={styles.hubTitle}>서지안 사건 파일</h1>
        <div className={styles.caseList}>
          {CHAPTERS.map((chapter, index) => {
            const { saved, done } = status[index];
            // Cases open in order: each one unlocks when the one before it is solved.
            const open = index === 0 || testMode || status[index - 1].done;
            const label = done ? '해결' : saved ? '수사 중' : open ? '새 사건' : '잠김';
            return (
              <button
                key={chapter.META.id}
                className={styles.caseCard}
                onClick={() => setOpenId(chapter.META.id)}
                disabled={!open}
              >
                <span className={styles.caseNumber}>{chapter.META.number}</span>
                <span className={styles.caseHanja}>{chapter.META.hanja}</span>
                <span className={styles.caseText}>
                  <span className={styles.caseName}>{chapter.META.name}</span>
                  <span className={styles.caseSubtitle}>
                    {open ? chapter.META.subtitle : '앞 사건을 해결하면 열립니다'}
                  </span>
                </span>
                <span className={`${styles.tag} ${done ? styles.tagDone : ''}`}>{label}</span>
              </button>
            );
          })}
          {UPCOMING.map((chapter) => (
            <button key={chapter.number} className={styles.caseCard} disabled>
              <span className={styles.caseNumber}>{chapter.number}</span>
              <span className={styles.caseHanja}>{chapter.hanja}</span>
              <span className={styles.caseText}>
                <span className={styles.caseName}>{chapter.name}</span>
              </span>
              <span className={styles.tag}>준비 중</span>
            </button>
          ))}
        </div>
        {isOwner && (
          <button className={styles.testToggle} onClick={toggleTest}>
            테스트 모드 {testOn ? '켜짐' : '꺼짐'}
            <span>{testOn ? '모든 사건이 열리고, 증거를 다 가진 채 시작하며, 정답이 표시됩니다.' : '다른 사람과 똑같이 플레이합니다.'}</span>
          </button>
        )}
        <button className={styles.backLink} onClick={() => navigate('/')}>← 항구로 돌아가기</button>
      </main>
    </div>
  );
}

export default Detective;
